import type { PipelineParamValue } from '@/interfaces/pipelines.interface';
import type { AnalysisRelationshipPath } from '@/helpers/analysisRelationshipPath.helpers';
import type { ParetoOptions } from '@/types/pareto.types';

export const ANALYSIS_RECIPE_SCHEMA_VERSION = 2 as const;

export type AnalysisJsonValue =
  string | number | boolean | null | AnalysisJsonValue[] | AnalysisJsonObject;

export interface AnalysisJsonObject {
  [key: string]: AnalysisJsonValue;
}

export type AnalysisSheetFace = 'data' | 'analysis';

/**
 * A search a sheet is sourced from.
 *
 * Single-label by necessity: an unscoped search returns a mixed-label set,
 * which the backend will not let a property projection read from.
 */
export interface AnalysisRecipeSearchSource {
  query: string;
  mode: 'keyword' | 'semantic';
  contains: boolean;
  filters?: AnalysisJsonObject[];
}

export interface AnalysisRecipeSource {
  kind: 'label' | 'exploration';
  label?: string;
  cohort?: AnalysisJsonObject;
  /**
   * Present when the sheet's rows come from a search rather than the whole
   * label. Added as an OPTIONAL field on the existing `label` kind rather than
   * as a new kind: a new kind fails `isSource`, which makes the recipe
   * unsaveable and makes a restore drop the analyst's entire recipe silently.
   * Absence is unambiguous, so no schema-version bump is needed.
   */
  search?: AnalysisRecipeSearchSource;
}

export interface AnalysisRecipeStep {
  id: string;
  /**
   * `join` pulls fields from a record matched by VALUE rather than by
   * relationship — `Stage.jobnumber = Job.node_id`, where the schema has no
   * edge to follow. It is a separate kind rather than a flag on `lookup`
   * because it compiles through a different backend primitive with different
   * constraints; see `compileAnalysisRecipe`.
   */
  kind: 'lookup' | 'rollup' | 'calc' | 'join';
  sourceLabel: string;
  outputColumns: string[];
  sentence: string;
  config: AnalysisJsonObject;
}

export interface AnalysisRecipeControl {
  id: string;
  paramName: string;
  field: string;
  operator: string;
  value: PipelineParamValue;
  applied: boolean;
}

export interface AnalysisBreakdownAggregation {
  id: string;
  field?: string;
  /**
   * `count_distinct` is the source-row-count grain for a group-by-related
   * breakdown: after the one-hop join fans a source out to every related record
   * it reaches, a plain `count` would count each (source, target) pair rather
   * than each source once. It always reads the parent's own `id`, never
   * `field` — the option exists so the auto "rows" metric can ask for it, not
   * so an author points it at an arbitrary column.
   */
  op: 'count' | 'sum' | 'avg' | 'min' | 'max' | 'count_distinct';
  outputColumn: string;
}

export interface AnalysisBreakdownWindow {
  id: string;
  op: 'percent_of_total' | 'rank';
  sourceColumn: string;
  outputColumn: string;
}

/**
 * A breakdown grouped by a related record's identity, reached via a path off
 * the parent sheet's source label, rather than by a column value.
 *
 * Grouping by a lookup's own output column is unsafe once the path is
 * one-to-many: the lookup collapses each row's matches to a single arbitrary
 * pick (`first`), so the value it groups on — and therefore which group a row
 * lands in — is not the same value run to run. Grouping by the related record
 * itself sidesteps that: the join keeps every match, a row is counted in
 * every group it actually reaches, and a row with no match becomes its own
 * named group instead of a silently arbitrary or blank one.
 */
export interface AnalysisBreakdownGroupByRelated {
  /** Path from the parent sheet's source label to the label being grouped by. */
  path: AnalysisRelationshipPath;
  /** Field on the related record shown as the group's label. */
  displayField: string;
  /** The related label, e.g. "Company" — names the "No Company" bucket and the chip. */
  targetLabel: string;
}

export interface AnalysisBreakdownLink {
  parentSheetId: string;
  /**
   * The breakdown's own output column for the group label. Present whether the
   * grouping came from a column value or from `groupByRelated` — every other part
   * of the sheet (aggregations, chips, windows, publication) keys off this name
   * and does not need to know which.
   *
   * Absent means a **total** sheet: no group key at all, one row for the whole
   * parent. `isAnalysisTotalSheet` is the predicate; do not test this field
   * directly, since a broken breakdown can also be missing its column.
   *
   * Deliberately optional rather than a separate `kind` discriminator: all eight
   * family-scoping helpers key off `sheet.breakdown` being truthy, so a total
   * sheet inherits correct view-state keying, discard cascades and family scoping
   * for free. No schema-version bump — every v2 recipe carries the column, so
   * absence is unambiguous.
   */
  groupByColumn?: string;
  /** Present when grouping by a related record's identity rather than a column value. */
  groupByRelated?: AnalysisBreakdownGroupByRelated;
  aggregations?: AnalysisBreakdownAggregation[];
  /** Metrics deliberately removed from the grouped output and available to restore. */
  removedAggregations?: AnalysisBreakdownAggregation[];
  windows?: AnalysisBreakdownWindow[];
  /**
   * Windows archived alongside the aggregation they read, so restoring the metric
   * restores its shares and ranks too. A window whose source aggregation is gone
   * compiles to a reference to a column the pipeline no longer produces, which
   * fails the run outright.
   */
  removedWindows?: AnalysisBreakdownWindow[];
  status?: 'live' | 'stale' | 'broken';
  brokenReason?: string;
}

export type AnalysisRecipeChartConfig = AnalysisJsonObject & ParetoOptions & {
  description?: string;
  xAxis?: string;
  yAxisColumns?: string[];
  seriesSplit?: string;
  xAxisGroupColumns?: string[];
  yAxisGroupColumns?: string[];
  heatmapValueColumn?: string;
  axisLabelWidth?: number;
  axisLabelWidthX?: number;
  axisLabelWidthY?: number;
  pieLabelWidth?: number;
  /**
   * Whether this chart draws the zoom slider under the plot. Absent means no, on
   * the reMap strip: the tile is small while authoring and the bar took height the
   * plot needed. Dashboard cards are unaffected — they do not read this.
   */
  showDataZoomSlider?: boolean;
};

export interface AnalysisRecipeChart {
  id: string;
  title: string;
  visualization: string;
  config: AnalysisRecipeChartConfig;
  ownParams?: Record<string, PipelineParamValue>;
}

export interface AnalysisSheetRecipe {
  id: string;
  name: string;
  face: AnalysisSheetFace;
  source: AnalysisRecipeSource;
  /**
   * Root sheet that owns this sheet's visible workset. Root sheets omit this
   * field; labels gathered into another analysis family point at that root.
   * Breakdown parentage remains represented separately by `breakdown`.
   */
  worksetRootSheetId?: string;
  steps: AnalysisRecipeStep[];
  keepFilter: AnalysisJsonObject | null;
  controls: AnalysisRecipeControl[];
  breakdown: AnalysisBreakdownLink | null;
  charts: AnalysisRecipeChart[];
  /**
   * Decimal places to display for a computed column, keyed by its output column.
   *
   * Display only — nothing here reaches the pipeline, so the value the backend
   * returned and everything computed from it are untouched. It lives on the
   * recipe rather than in component state so a chosen precision survives a save
   * and a navigation away, and so publication can hand the same number to the
   * card: the sheet and the card it produced must not disagree about how many
   * decimals a figure has.
   *
   * Absent means "use the format's own default" — one decimal for a percentage,
   * `toLocaleString` for a measured number. Absent is not zero.
   */
  columnPrecision?: Record<string, number>;
}

/**
 * Canonical, Studio-persistable Analysis Sheet state.
 *
 * Runtime table rows, loading flags, pagination, and transient dialogs are
 * intentionally excluded. They belong to the table/UI layers rather than the
 * durable analysis recipe.
 */
export interface AnalysisRecipeState {
  schemaVersion: typeof ANALYSIS_RECIPE_SCHEMA_VERSION;
  activeSheetId: string | null;
  sheetOrder: string[];
  sheets: Record<string, AnalysisSheetRecipe>;
}
